/**
 * ONE MINUTE TICK FOR THE WHOLE APP — the half that keeps a rendered clock from lying.
 *
 * 🔴 **W20(e)'s OWN SPEC GOT THIS WRONG AND IT IS WORTH SAYING SO.** It reads: *"The clock is a
 * RENDER of a zone and a moment, not a stored time: it is recomputed per paint, so it needs no
 * refresh and cannot go stale in the way a stored string would."* The first half is true and the
 * conclusion does not follow — React does not paint on its own. A coach who opens a client page
 * and leaves the tab open reads `9:48 AM` at half past eleven, which is precisely the wrong-time
 * failure the whole feature exists to prevent, arrived at from the other direction: not a
 * guessed zone, a correct zone frozen at the moment the page happened to load.
 *
 * ⚠️ **ONE INTERVAL, NOT ONE PER CLOCK.** The Inbox rail draws a clock per client, so a hook
 * that opened its own `setInterval` would open one per row — ten timers doing identical work and
 * waking the tab ten times a minute. `useSyncExternalStore` over a module-level ticker gives
 * every subscriber the same instant from one timer, started on the first subscription and
 * cleared on the last.
 *
 * ⚠️ **MINUTE GRANULARITY, AND IT ALIGNS TO THE MINUTE BOUNDARY** rather than firing 60 s after
 * whenever the first component mounted. A clock that changes at :17 past each minute is a clock
 * that is wrong for up to 59 seconds — invisible most of the time and exactly the kind of small
 * lie this file exists to remove. Nothing here shows seconds, so nothing needs a faster tick.
 */

type Listener = () => void;

const listeners = new Set<Listener>();
let timer: ReturnType<typeof setTimeout> | null = null;

// ⚠️ THE SNAPSHOT IS A STABLE REFERENCE BETWEEN TICKS. `useSyncExternalStore` compares
// snapshots with `Object.is` and will loop for ever if `getSnapshot` returns a fresh object
// every call — so a new `Date` is created on the TICK, never in the getter.
let now = new Date();

function scheduleNextMinute(): void {
  const ms = 60_000 - (Date.now() % 60_000);
  timer = setTimeout(() => {
    now = new Date();
    for (const l of listeners) l();
    if (listeners.size > 0) scheduleNextMinute();
    else timer = null;
  }, ms);
}

export function subscribeToMinute(listener: Listener): () => void {
  listeners.add(listener);
  if (timer === null) scheduleNextMinute();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };
}

/** The current instant, stable until the next minute boundary. */
export function getMinuteSnapshot(): Date {
  return now;
}

/**
 * The server snapshot `useSyncExternalStore` asks for. This app is a static SPA and never
 * renders on a server, but React calls this during hydration-shaped paths and a getter that
 * returned a fresh `Date` here would be the same infinite-loop defect one branch over.
 */
export function getMinuteServerSnapshot(): Date {
  return now;
}
